import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import "./RelatorioCompras.css";

import {
  Button,
  TextField,
  Grid,
  MenuItem,
  Typography,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Container
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import dayjs from 'dayjs';
import api from '../../../services/api';
import API_CONFIG from '../../../config/api.config';

const RelatorioCompras = () => {
  const navigate = useNavigate();
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [prefeituraId, setPrefeituraId] = useState('');

  // Verificação de autenticação e tipo de usuário
  useEffect(() => {
    const tipoUsuario = localStorage.getItem('tipoUsuario');
    const idUsuario = localStorage.getItem('idUsuario');
    const prefeituraIdFromStorage = localStorage.getItem('idPrefeitura');

    if (!tipoUsuario || !idUsuario) {
      //console.log('Usuário não autenticado - Redirecionando para login');
      navigate('/', { replace: true });
      return;
    }

    if (tipoUsuario !== 'PREFEITURA') {
      //console.log(`Tipo de usuário (${tipoUsuario}) não corresponde ao permitido (PREFEITURA)`);
      navigate('/', { replace: true });
      return;
    }

    // Pegar o ID da prefeitura do localStorage
    if (prefeituraIdFromStorage) {
      setPrefeituraId(prefeituraIdFromStorage);
    }

    //console.log('Prefeitura autenticada:', idUsuario);
  }, [navigate]);

  useEffect(() => {
    const handleMenuChange = (event) => {
      setIsMenuExpanded(!event.detail.isCollapsed);
    };
    window.addEventListener('menuStateChange', handleMenuChange);
    return () => window.removeEventListener('menuStateChange', handleMenuChange);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLimparFiltros = () => {
    setDataInicio('');
    setDataFim('');
    setError('');
    setSuccess('');
  };

  const handleDownloadPDF = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Verificar se o prefeituraId está disponível
      if (!prefeituraId) {
        setError('ID da prefeitura não encontrado. Faça login novamente.');
        return;
      }
      
      const params = {};
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;
      
      // Usar PRE_ID conforme solicitado
      if (prefeituraId) {
        params.PRE_ID = parseInt(prefeituraId);
        // Se não funcionar, tentar como string
        if (isNaN(params.PRE_ID)) {
          params.PRE_ID = prefeituraId;
        }
      }

      //console.log('Parâmetros sendo enviados:', params);
      //console.log('ID da prefeitura:', prefeituraId);
      //console.log('Tipo do PRE_ID:', typeof params.PRE_ID);
      //console.log('Token de autenticação:', localStorage.getItem('token'));

      const response = await api.get(API_CONFIG.ENDPOINTS.RELATORIOS.COMPRAS_PRE, {
        params,
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      // Verificar se a resposta é um PDF válido
      if (response.data.type && response.data.type.includes('application/pdf')) {
        // Criar URL do blob
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        
        // Detectar se o navegador consegue renderizar PDFs
        const canRenderPDF = () => {
          // Chrome, Edge, Firefox modernos conseguem renderizar PDFs
          const userAgent = navigator.userAgent.toLowerCase();
          return userAgent.includes('chrome') || 
                 userAgent.includes('edge') || 
                 userAgent.includes('firefox');
        };

        if (canRenderPDF()) {
          // Tentar abrir em nova aba
          const newWindow = window.open(url, '_blank');
          
          if (newWindow) {
            // Aguardar um pouco para ver se conseguiu abrir
            setTimeout(() => {
              if (newWindow.closed) {
                // Se a janela foi fechada rapidamente, provavelmente não conseguiu renderizar
                downloadPDF(url);
              }
            }, 1000);
            
            setSuccess('Relatório aberto em nova aba!');
          } else {
            // Popup bloqueado, fazer download
            downloadPDF(url);
            setSuccess('Relatório baixado com sucesso! (popup bloqueado)');
          }
        } else {
          // Navegadores que não conseguem renderizar PDFs, fazer download direto
          downloadPDF(url);
          setSuccess('Relatório baixado com sucesso!');
        }
        
        // Limpar URL após um tempo
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 5000);
      } else {
        // Se não for PDF, tentar ler como JSON para pegar mensagem de erro
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            setError(errorData.message || 'Erro ao gerar relatório');
          } catch {
            setError('Erro ao gerar relatório PDF');
          }
        };
        reader.readAsText(response.data);
      }
    } catch (error) {
     // console.error('Erro ao baixar PDF:', error);
     // console.error('Status do erro:', error.response?.status);
     // console.error('Dados do erro:', error.response?.data);
      
      // Tratamento específico para erro 422
      if (error.response?.status === 422) {
        try {
          // Tentar ler a resposta como JSON para pegar a mensagem de erro
          const errorText = await error.response.data.text();
         // console.error('Resposta de erro 422:', errorText);
          
          try {
            const errorData = JSON.parse(errorText);
            setError(errorData.message || errorData.error || 'Dados inválidos para gerar relatório');
          } catch (parseError) {
           // console.error('Erro ao fazer parse do JSON:', parseError);
            setError(`Dados inválidos para gerar relatório: ${errorText}`);
          }
        } catch (textError) {
         // console.error('Erro ao ler resposta como texto:', textError);
          setError('Dados inválidos para gerar relatório (erro 422)');
        }
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.data) {
        // Se data for um objeto, tentar converter para string
        if (typeof error.response.data === 'object') {
          setError('Erro ao gerar relatório PDF');
        } else {
          setError(String(error.response.data));
        }
      } else {
        setError('Erro ao gerar relatório PDF');
      }
    } finally {
      setLoading(false);
    }
  };

  // Função auxiliar para fazer download do PDF
  const downloadPDF = (url) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio-compras-${new Date().toISOString().split('T')[0]}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className={`relatorio-compras-container ${isMenuExpanded ? 'menu-expanded' : ''}`}>
      <div className="relatorio-compras-wrapper">
        <h1 className="relatorio-compras-title">Relatório de Compras</h1>
        
        <Container maxWidth="lg" className="relatorio-compras-content">
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Card className="relatorio-compras-card">
            <CardContent>
              <Typography variant="h6" gutterBottom className="relatorio-compras-subtitle">
                Filtros do Relatório
              </Typography>
              
              <Grid container spacing={3} sx={{ mb: 4 }} justifyContent="center" alignItems="center">
                <Grid item xs={12} sm={6} md={2.5}>
                  <TextField
                    label="Data Início"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.5}>
                  <TextField
                    label="Data Fim"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.5}>
                  <Button
                    variant="outlined"
                    onClick={handleLimparFiltros}
                    disabled={loading}
                    size="large"
                    fullWidth
                  >
                    Limpar Filtros
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleDownloadPDF}
                    disabled={loading}
                    size="large"
                    className="relatorio-compras-export-button"
                    fullWidth
                  >
                    {loading ? 'Gerando PDF...' : 'Abrir Relatório PDF'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card className="relatorio-compras-info-card">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informações do Relatório
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Este relatório permite gerar um documento PDF com todas as compras realizadas pela prefeitura, 
                filtradas por período. O relatório inclui informações detalhadas sobre cada compra, 
                incluindo fornecedor, valores e status.
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </div>
    </div>
  );
};

export default RelatorioCompras;